// src/components/sections/ErpOverview.styles.js

const getResponsiveValue = (mobile, tablet, desktop) => {
  if (typeof window === "undefined") return desktop;

  const width = window.innerWidth;
  if (width <= 640) return mobile;
  if (width <= 768) return tablet;
  return desktop;
};

const styles = {
  // Main section styling
  section: (isDarkMode) => ({
    paddingTop: getResponsiveValue("3rem", "4rem", "5rem"),
    paddingBottom: getResponsiveValue("3rem", "4rem", "5rem"),
    position: "relative",
    overflow: "hidden",
    backgroundColor: isDarkMode
      ? "rgba(17, 24, 39, 0.9)"
      : "rgba(249, 250, 251, 0.9)",
    minHeight: "100vh",
  }),

  // Floating background elements
  floatingElement: (elem) => ({
    position: "absolute",
    top: elem.top,
    left: elem.left,
    width: `${elem.size}px`,
    height: `${elem.size}px`,
    borderRadius: "50%",
    backgroundColor: elem.color,
    filter: "blur(8px)",
    zIndex: 0,
  }),

  container: {
    maxWidth: "1200px",
    margin: "0 auto",
    padding: getResponsiveValue("0 1rem", "0 1.5rem", "0 2rem"),
    position: "relative",
    zIndex: 1,
  },

  // Header styling
  header: {
    textAlign: "center",
    marginBottom: getResponsiveValue("2rem", "3rem", "4rem"),
    maxWidth: "800px",
    margin: "0 auto",
    paddingBottom: getResponsiveValue("2rem", "3rem", "4rem"),
  },

  heading: (isDarkMode) => ({
    fontSize: `clamp(2rem, 5vw, 3.5rem)`,
    fontWeight: 800,
    marginBottom: getResponsiveValue("1rem", "1.25rem", "1.5rem"),
  }),

  accent: {
    color: "#3b82f6",
  },

  subheading: (isDarkMode) => ({
    fontSize: `clamp(1rem, 2vw, 1.25rem)`,
    color: isDarkMode ? "rgba(255,255,255,0.9)" : "rgba(0,0,0,0.8)",
    lineHeight: 1.6,
    padding: getResponsiveValue("0 1rem", "0", "0"),
  }),

  // Modules grid styling
  modulesGrid: {
    display: "flex",
    flexWrap: "wrap",
    justifyContent: getResponsiveValue("center", "flex-start", "flex-start"),
    gap: getResponsiveValue("1rem", "1.5rem", "2rem"),
    marginBottom: getResponsiveValue("3rem", "4rem", "5rem"),
  },

  moduleCard: (isDarkMode) => ({
    position: "relative",
    backgroundColor: isDarkMode
      ? "rgba(31, 41, 55, 0.8)"
      : "rgba(255, 255, 255, 0.9)",
    backdropFilter: "blur(10px)",
    borderRadius: getResponsiveValue("0.75rem", "1rem", "1rem"),
    border: `1px solid ${
      isDarkMode ? "rgba(255,255,255,0.1)" : "rgba(0,0,0,0.05)"
    }`,
    boxShadow: isDarkMode
      ? "0 10px 25px -5px rgba(0, 0, 0, 0.3), 0 8px 10px -6px rgba(0, 0, 0, 0.2)"
      : "0 10px 25px -5px rgba(0, 0, 0, 0.1), 0 8px 10px -6px rgba(0, 0, 0, 0.05)",
    transition: "transform 0.3s ease, box-shadow 0.3s ease",
    overflow: "hidden",
    height: "100%",
  }),

  cardContent: {
    position: "relative",
    zIndex: 2,
    padding: getResponsiveValue("1.5rem", "1.75rem", "2rem"),
    display: "flex",
    flexDirection: "column",
    height: "100%",
  },

  iconContainer: (color, isDarkMode) => ({
    width: getResponsiveValue("3.5rem", "3.75rem", "4rem"),
    height: getResponsiveValue("3.5rem", "3.75rem", "4rem"),
    borderRadius: "1rem",
    backgroundColor: isDarkMode ? `${color}15` : `${color}10`,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    marginBottom: getResponsiveValue("1rem", "1.25rem", "1.5rem"),
    position: "relative",
    overflow: "hidden",
    border: `1px solid ${color}30`,
  }),

  iconPulse: {
    width: "100%",
    height: "100%",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
  },

  cardHeader: {
    flex: 1,
  },

  moduleTitle: (isDarkMode) => ({
    fontSize: getResponsiveValue("1.25rem", "1.375rem", "1.5rem"),
    fontWeight: 700,
    marginBottom: getResponsiveValue("0.75rem", "0.875rem", "1rem"),
    color: isDarkMode ? "#f9fafb" : "#1f2937",
  }),

  moduleDescription: (isDarkMode) => ({
    fontSize: getResponsiveValue("0.9375rem", "0.9375rem", "1rem"),
    color: isDarkMode ? "rgba(255,255,255,0.7)" : "rgba(0,0,0,0.7)",
    lineHeight: 1.6,
  }),

  cardDecoration: (isDarkMode, color) => ({
    position: "absolute",
    top: 0,
    right: 0,
    bottom: 0,
    left: 0,
    zIndex: 1,
    overflow: "hidden",
    borderRadius: "1rem",
  }),

  decorCircle: (color) => ({
    position: "absolute",
    top: "-20%",
    right: "-20%",
    width: "60%",
    height: "60%",
    borderRadius: "50%",
    border: `1px dashed ${color}30`,
  }),

  decorCircleOuter: (color) => ({
    position: "absolute",
    bottom: "-30%",
    left: "-30%",
    width: "80%",
    height: "80%",
    borderRadius: "50%",
    border: `1px dashed ${color}20`,
  }),

  // Hub visualization (center display)
  hubVisualization: {
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    marginBottom: getResponsiveValue("3rem", "4rem", "5rem"),
    height: getResponsiveValue("250px", "300px", "400px"),
    position: "relative",
  },

  hubContainer: (isDarkMode) => ({
    position: "relative",
    width: getResponsiveValue("250px", "300px", "400px"),
    height: getResponsiveValue("250px", "300px", "400px"),
    borderRadius: "50%",
    backgroundColor: isDarkMode
      ? "rgba(17, 24, 39, 0.3)"
      : "rgba(249, 250, 251, 0.5)",
    border: `1px solid ${
      isDarkMode ? "rgba(255,255,255,0.1)" : "rgba(0,0,0,0.05)"
    }`,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    boxShadow: isDarkMode
      ? "0 20px 25px -5px rgba(0, 0, 0, 0.3), 0 10px 10px -5px rgba(0, 0, 0, 0.2)"
      : "0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.05)",
  }),

  hubInner: {
    position: "relative",
    width: getResponsiveValue("80px", "100px", "120px"),
    height: getResponsiveValue("80px", "100px", "120px"),
    borderRadius: "50%",
    backgroundColor: "rgba(59, 130, 246, 0.1)",
    border: "2px solid rgba(59, 130, 246, 0.3)",
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    justifyContent: "center",
    zIndex: 10,
  },

  hubText: (isDarkMode) => ({
    fontSize: getResponsiveValue("0.75rem", "0.8125rem", "0.875rem"),
    fontWeight: 600,
    marginTop: getResponsiveValue("0.25rem", "0.375rem", "0.5rem"),
    color: isDarkMode ? "rgba(255,255,255,0.9)" : "rgba(0,0,0,0.8)",
  }),

  // Connection lines from hub to module labels
  hubConnection: (index, color) => {
    const angle = index * 60 * (Math.PI / 180);
    const transform = `rotate(${index * 60}deg)`;
    const width = getResponsiveValue("100px", "120px", "150px");

    return {
      position: "absolute",
      top: "50%",
      left: "50%",
      width: width,
      height: "2px",
      backgroundColor: `${color}40`,
      transform: transform,
      transformOrigin: "0 0",
      zIndex: 1,
    };
  },

  connectionDot: (color) => ({
    position: "absolute",
    top: "0",
    width: getResponsiveValue("4px", "5px", "6px"),
    height: getResponsiveValue("4px", "5px", "6px"),
    borderRadius: "50%",
    backgroundColor: color,
    transform: "translateY(-50%)",
  }),

  // Module labels around the hub
  moduleLabel: (index, color, isDarkMode) => {
    const angle = index * 60;
    const angleInRad = (angle - 90) * (Math.PI / 180);

    const radius = getResponsiveValue(110, 130, 170);
    const x = Math.cos(angleInRad) * radius;
    const y = Math.sin(angleInRad) * radius;

    return {
      position: "absolute",
      top: "50%",
      left: "50%",
      transform: `translate(calc(${x}px - 50%), calc(${y}px - 50%))`,
      backgroundColor: isDarkMode ? "rgba(31, 41, 55, 0.9)" : "white",
      border: `1px solid ${color}40`,
      borderRadius: "0.75rem",
      padding: getResponsiveValue("0.375rem", "0.5rem", "0.75rem"),
      display: "flex",
      flexDirection: "column",
      alignItems: "center",
      gap: "0.25rem",
      boxShadow: isDarkMode
        ? "0 4px 6px -1px rgba(0, 0, 0, 0.2), 0 2px 4px -1px rgba(0, 0, 0, 0.1)"
        : "0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)",
      zIndex: 5,
    };
  },

  labelText: (isDarkMode) => ({
    fontSize: getResponsiveValue("0.625rem", "0.6875rem", "0.75rem"),
    fontWeight: 600,
    color: isDarkMode ? "rgba(255,255,255,0.9)" : "rgba(0,0,0,0.8)",
  }),

  // CTA styling
  ctaContainer: {
    display: "flex",
    justifyContent: "center",
    marginTop: getResponsiveValue("1.5rem", "1.75rem", "2rem"),
    padding: getResponsiveValue("0 1rem", "0", "0"),
  },

  ctaButton: {
    backgroundColor: "#3b82f6",
    background: "linear-gradient(to right, #3b82f6, #60a5fa)",
    color: "white",
    padding: getResponsiveValue(
      "0.875rem 1.5rem",
      "0.9375rem 1.75rem",
      "1rem 2rem"
    ),
    borderRadius: getResponsiveValue("0.5rem", "0.625rem", "0.75rem"),
    textDecoration: "none",
    fontWeight: 600,
    fontSize: getResponsiveValue("1rem", "1.0625rem", "1.125rem"),
    display: "inline-flex",
    alignItems: "center",
    gap: "0.5rem",
    transition: "all 0.3s ease",
    boxShadow:
      "0 4px 6px -1px rgba(59, 130, 246, 0.3), 0 2px 4px -1px rgba(59, 130, 246, 0.2)",
    width: getResponsiveValue("100%", "auto", "auto"),
    justifyContent: "center",
  },
};

export default styles;
